import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deliveryQualityService } from 'services/deliveryQualityService';
import { Modal } from '../../../../components/ui/Modal';
import { Badge } from '../../../../components/ui/Badge';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { CopyButton } from '../../../../components/ui/CopyButton';
import { cn } from '../../../../utils/cn';

// Mapa de veredicto / confianza → tono semántico (tokens, dark mode automático).
const VERDICT_TONE = {
  'ENTREGA VALIDADA': 'success',
  'ENTREGA DUDOSA': 'warning',
  'ENTREGA RECHAZADA': 'error',
  'ENTREGA INVALIDA': 'error',
  'ENTREGA INVÁLIDA': 'error',
};

const CONFIDENCE_TONE = {
  ALTO: 'success',
  MEDIO: 'warning',
  BAJO: 'error',
};

const SUBCRITERION_LABELS = {
  packageLabelAi: 'Etiqueta del paquete',
  addressNumberAi: 'Número de domicilio',
  receiverEvidenceAi: 'Receptor / lugar de entrega',
};

// Criterios que componen el score total ADEMÁS del análisis IA de fotos. El doc
// de getScoreByPackage trae cada uno con su sub-score, el valor capturado (RUT /
// nombre / metros / hora / nº de fotos) y un `reason` con la explicación del
// backend. Estructura tomada del shape real de la API.
const DETERMINISTIC_CRITERIA = [
  { key: 'rut',        label: 'RUT del receptor' },
  { key: 'receiver',   label: 'Nombre del receptor' },
  { key: 'distance',   label: 'Distancia al punto' },
  { key: 'timeliness', label: 'Puntualidad' },
  { key: 'imageCount', label: 'Cantidad de fotos' },
];

const fmtMeters = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);
const fmtHourDecimal = (h) => {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};
// Fecha desde deliveredAtLocal (sliceada, sin Date para no re-desplazar TZ).
const fmtDateOnly = (v) => {
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(v).slice(0, 10);
};
// Hora desde deliveredAtLocal (solo fallback si no viene hourDecimal).
const fmtTimeOnly = (v) => {
  const m = String(v).match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
};

// Arma la vista de un criterio a partir de su sub-objeto en doc.scores: estado
// (ok), sub-score, una línea de detalle con el valor capturado, el `reason` del
// backend y (distancia) un link al punto en el mapa.
function criterionView(key, scores, t) {
  const c = (scores && scores[key]) || {};
  const score = typeof c.score === 'number' ? c.score : null;
  const reason = c.reason || null;
  switch (key) {
    case 'rut': {
      const value = String(c.value || '').trim();
      return {
        ok: c.valid, score, reason,
        detail: value ? `${t('RUT ingresado')}: ${c.value}` : t('No se registró RUT'),
      };
    }
    case 'receiver': {
      const value = String(c.value || '').trim();
      return {
        ok: c.valid, score, reason,
        detail: value ? `${t('Nombre ingresado')}: ${c.value}` : t('No se registró nombre'),
      };
    }
    case 'distance': {
      const coords = c.deliveryCoords;
      const mapsHref = coords && coords.lat != null && coords.lng != null
        ? `https://www.google.com/maps?q=${coords.lat},${coords.lng}`
        : null;
      return {
        ok: c.withinThreshold, score, reason, mapsHref,
        detail: typeof c.meters === 'number' ? `${fmtMeters(c.meters)} ${t('del punto de entrega')}` : null,
      };
    }
    case 'timeliness': {
      // La hora local correcta es hourDecimal (coincide con el `reason` del back).
      // deliveredAtLocal trae el wall-clock en UTC pese al nombre, así que para la
      // HORA usamos hourDecimal y sólo tomamos la FECHA de deliveredAtLocal.
      const datePart = c.deliveredAtLocal ? fmtDateOnly(c.deliveredAtLocal) : null;
      const timePart = typeof c.hourDecimal === 'number'
        ? fmtHourDecimal(c.hourDecimal)
        : (c.deliveredAtLocal ? fmtTimeOnly(c.deliveredAtLocal) : null);
      const when = [datePart, timePart].filter(Boolean).join(' ');
      return { ok: c.withinGrace, score, reason, detail: when ? `${t('Entregado')}: ${when}` : null };
    }
    case 'imageCount': {
      const ok = c.minRequired != null ? (c.count ?? 0) >= c.minRequired : undefined;
      return {
        ok, score, reason,
        detail: c.count != null ? `${c.count} ${t('de')} ${c.minRequired ?? '?'} ${t('fotos mínimas')}` : null,
      };
    }
    default:
      return { ok: undefined, score, reason, detail: null };
  }
}

const PackageAiDetailModal = ({ packageId, followingNumber, isOpen, toggle }) => {
  const { t } = useTranslation('deliveryquality');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    if (!isOpen || !packageId) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    setDoc(null);
    deliveryQualityService
      .getScoreByPackage(packageId)
      .then((r) => alive && setDoc(r.data))
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        if (status === 404) {
          setError(t('La IA está analizando esta entrega. Cuando termine el análisis vas a poder ver el detalle acá.'));
        } else {
          setError(e?.message || t('Error cargando análisis IA'));
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [isOpen, packageId]);

  const scores = doc?.scores || {};
  // Pesos por criterio (puntos sobre 100; los criterios suman 100). Permiten
  // mostrar cuánto aporta cada cosa al score total en vez de un "/100" plano.
  const weights = doc?.weights || {};
  const imagesAi = scores.imagesAi || {};
  const aiJob = imagesAi.aiJob || {};
  const result = aiJob.result || {};
  const verdict = imagesAi.verdict || result.resultado_final;
  const confidence = imagesAi.confidence || result.nivel_confianza;
  const evidencia = result.evidencia_detectada || [];
  const images = imagesAi.imagesAnalyzed || aiJob.imagesSent || [];

  const renderSub = (key) => {
    const sub = imagesAi[key];
    if (!sub) return null;
    const isValid = (sub.estado || '').toUpperCase() === 'VALIDO';
    return (
      <div key={key} className="border border-border rounded-lg p-2 h-full bg-bg">
        <div className="flex justify-between items-center mb-1">
          <strong className="text-[13px] text-fg">{t(SUBCRITERION_LABELS[key])}</strong>
          <Badge tone={isValid ? 'success' : 'error'}>{sub.estado || '—'}</Badge>
        </div>
        <div className="text-[22px] font-bold text-fg tabular-nums">{sub.score ?? 0}</div>
        {sub.detalle && <div className="text-xs text-fg-muted">{sub.detalle}</div>}
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={toggle}
      size="lg"
      title={
        <div>
          <div className="text-base font-semibold text-fg">{t('Análisis IA del paquete')}</div>
          {followingNumber != null && followingNumber !== '' ? (
            <span className="inline-flex items-center gap-0.5">
              <code className="text-[11px] text-fg-subtle" title={packageId || undefined}>
                #{followingNumber}
              </code>
              <CopyButton
                value={followingNumber}
                label={t('Copiar número de seguimiento')}
                copiedMessage={t('Número de seguimiento copiado')}
              />
            </span>
          ) : (
            <code className="text-[11px] text-fg-subtle">{packageId}</code>
          )}
        </div>
      }
    >
      {loading && <Skeleton className="h-40 rounded-lg" />}
      {error && (
        <div className="rounded-lg border border-status-warning-soft bg-status-warning-soft text-status-warning px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {!loading && !error && doc && (
        <>
          <div className="flex flex-wrap gap-2 mb-3 items-center">
            {verdict && <Badge tone={VERDICT_TONE[verdict] || 'neutral'}>{verdict}</Badge>}
            {confidence && (
              <Badge tone={CONFIDENCE_TONE[(confidence || '').toUpperCase()] || 'neutral'}>
                {t('Confianza')} {confidence}
              </Badge>
            )}
            <span className="ml-auto text-[13px] text-fg-muted">
              {t('Score total')}:{' '}
              <strong className="text-lg text-fg tabular-nums">{doc.totalScore ?? 0}</strong> / 100
            </span>
          </div>

          {/* Criterios de la entrega: el resto de los puntos del score además de
              las fotos. Cada uno con su sub-score, el valor capturado (RUT escrito,
              nombre, metros, hora, nº de fotos) y el `reason` que explica el por qué. */}
          <div className="mb-3">
            <h6 className="mb-2 text-sm font-semibold text-fg">{t('Criterios de la entrega')}</h6>
            <div className="flex flex-col gap-1.5">
              {DETERMINISTIC_CRITERIA.map((c) => {
                if (!scores[c.key]) return null;
                const v = criterionView(c.key, scores, t);
                const tone = v.ok === true ? 'success' : v.ok === false ? 'error' : 'neutral';
                const okLabel = v.ok === true ? t('Cumple') : v.ok === false ? t('No cumple') : t('Sin dato');
                // Peso del criterio (puntos sobre el total). Mostramos puntos
                // aportados / peso; los que pesan 0 no afectan el score.
                const w = weights[c.key];
                let weightNode = null;
                if (typeof w === 'number') {
                  if (w === 0) {
                    weightNode = <span className="ml-auto text-[11px] text-fg-subtle">{t('no pondera')}</span>;
                  } else {
                    const earned = Math.round((w * (v.score ?? 0)) / 100);
                    weightNode = (
                      <span className="ml-auto text-[11px] text-fg-subtle tabular-nums" title={t('Puntos que aporta al score total')}>
                        {earned}/{w} {t('pts')}
                      </span>
                    );
                  }
                } else if (v.score != null) {
                  weightNode = <span className="ml-auto text-[11px] text-fg-subtle tabular-nums">{v.score}/100</span>;
                }
                return (
                  <div key={c.key} className="border border-border rounded-lg p-2.5 bg-bg">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge tone={tone}>{okLabel}</Badge>
                      <span className="text-[13px] font-semibold text-fg">{t(c.label)}</span>
                      {weightNode}
                    </div>
                    {v.detail && <div className="mt-1 text-[13px] text-fg break-words">{v.detail}</div>}
                    {v.reason && <div className="mt-0.5 text-[12px] text-fg-muted break-words">{v.reason}</div>}
                    {v.mapsHref && (
                      <a
                        href={v.mapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-block text-[12px] text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        {t('Ver punto de entrega en el mapa')}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <h6 className="mb-2 text-sm font-semibold text-fg flex items-center gap-2 flex-wrap">
            <span>{t('Análisis IA de las fotos')}</span>
            {typeof weights.imagesAi === 'number' && weights.imagesAi > 0 && (
              <span className="text-[11px] font-normal text-fg-subtle tabular-nums">
                {t('aporta')} {weights.imagesAi} {t('pts')}
              </span>
            )}
          </h6>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            {Object.keys(SUBCRITERION_LABELS).map((k) => (
              <React.Fragment key={k}>
                {renderSub(k) || (
                  <div className={cn('border border-border rounded-lg p-2 h-full bg-bg-muted opacity-60')}>
                    <strong className="text-[13px] text-fg">{t(SUBCRITERION_LABELS[k])}</strong>
                    <div className="text-xs text-fg-subtle">{t('Sin sub-score IA')}</div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {evidencia.length > 0 && (
            <div className="mb-3">
              <h6 className="mb-2 text-sm font-semibold text-fg">{t('Evidencia detectada')}</h6>
              <ul className="mb-0 text-[13px] text-fg list-disc pl-5">
                {evidencia.map((e, i) => (
                  <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
                ))}
              </ul>
            </div>
          )}

          {images.length > 0 && (
            <div className="mb-3">
              <h6 className="mb-2 text-sm font-semibold text-fg">{t('Imágenes analizadas ({{n}})', { n: images.length })}</h6>
              <div className="flex flex-wrap gap-2">
                {images.map((src, i) => (
                  <a key={i} href={src} target="_blank" rel="noopener noreferrer" title={src}>
                    <img
                      src={src}
                      alt={`evidencia-${i}`}
                      className="w-[110px] h-[110px] object-cover rounded-md border border-border dark-safe-img"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {aiJob.status && (
            <div className="mb-3 text-xs text-fg-muted flex items-center gap-2 flex-wrap">
              <Badge tone="neutral">AI job: {aiJob.status}</Badge>
              {aiJob.modelName && <span>{t('Modelo')}: {aiJob.modelName} · </span>}
              {aiJob.durationMs != null && <span>{aiJob.durationMs} ms</span>}
            </div>
          )}

          <details>
            <summary className="cursor-pointer font-semibold text-fg text-sm">
              {t('Ver JSON completo del result IA')}
            </summary>
            <pre className="text-[11px] bg-bg-muted text-fg p-3 rounded-md max-h-[400px] overflow-auto mt-2">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </>
      )}
    </Modal>
  );
};

export default PackageAiDetailModal;
